import { useFetchConnectedIntegrations } from '@modules/integrationsV2/hooks';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  getIsCohortIntegrationAlreadyConnected,
  getIsUserOrAccountIntegrationAlreadyConnected,
} from '@modules/integrationsV2/utils';
import { CheckCircleRounded, InfoOutlined } from '@mui/icons-material';
import { Box, Chip, Stack, Tooltip } from '@mui/material';
import { IntegrationChannelType, IntegrationType } from 'generated/graphql';
import { toTitleCase } from 'v2/utils/strings';

interface ConnectedIntegrationChipProps {
  integrationType: IntegrationType;
  channelType: IntegrationChannelType;
}

export const ConnectedIntegrationChip = ({
  integrationType,
  channelType,
}: ConnectedIntegrationChipProps) => {
  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const { connectedIntegrations } = useFetchConnectedIntegrations({
    fetchPolicy: 'cache-first',
  });

  const isCohortIntegrationAlreadyConnected =
    getIsCohortIntegrationAlreadyConnected({
      connectedIntegrations,
      channelType,
    });

  const isUserOrAccountIntegrationAlreadyConnected =
    getIsUserOrAccountIntegrationAlreadyConnected({
      connectedIntegrations,
      integrationType,
    });

  if (
    !isCohortIntegrationAlreadyConnected &&
    !isUserOrAccountIntegrationAlreadyConnected
  ) {
    return null;
  }

  const getTooltipTitle = () => {
    if (isCohortIntegrationAlreadyConnected) {
      return (
        <Stack>
          <Box>You can only add a single integration for {channelType}.</Box>
          <Box>To connect another, please disable the existing one.</Box>
        </Stack>
      );
    }

    if (isUserOrAccountIntegrationAlreadyConnected) {
      return (
        <Stack>
          <Box>
            Currently, you can only add a single integration for{' '}
            {toTitleCase(integrationType)} type.
          </Box>
          <Box>To connect another, please disable the existing one.</Box>
        </Stack>
      );
    }

    return null;
  };

  const goToIntegrationDetailView = () => {
    const activeIntegration = connectedIntegrations.find(
      (integration) =>
        integration.channel.type === channelType &&
        integration.integrationType === integrationType &&
        integration.isActive,
    );

    if (!activeIntegration) {
      return;
    }

    dispatch({
      type: IntegrationsActionType.SET_VIEW,
      payload: {
        selectedIntegrationSourceOption: {
          integrationType: activeIntegration.integrationType,
          channelType: activeIntegration.channel.type,
          integrationID: activeIntegration.id,
          namespace: activeIntegration.namespace,
        },
        isAddDrawerOpen: false,
      },
    });
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Tooltip arrow title="View details" placement="top">
        <Chip
          variant="outlined"
          color="success"
          label="Connected"
          icon={<CheckCircleRounded />}
          onClick={goToIntegrationDetailView}
        />
      </Tooltip>
      <Tooltip arrow title={getTooltipTitle()} placement="top">
        <InfoOutlined
          fontSize="small"
          sx={{
            color: 'text.secondary',
          }}
        />
      </Tooltip>
    </Stack>
  );
};
